import { useNavigate } from 'react-router-dom';
import { Notification } from '@/types/notification';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useNotificationClick = () => {
  const navigate = useNavigate();

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.actionUrl) return;

    // Handle tournament notifications with robust fallback
    if (notification.type === 'tournament') {
      const url = notification.actionUrl;
      const hasId = url.includes('/tournaments/');

      if (hasId) {
        const tournamentId = url.split('/tournaments/')[1];
        if (tournamentId && tournamentId !== 'undefined') {
          return navigate(url);
        }
      }

      // Covers bare '/tournaments' or missing/undefined ID
      try {
        const { data: latestTournament, error } = await supabase
          .from('tournaments')
          .select('id')
          .eq('is_completed', true)
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error || !latestTournament) {
          toast.error('No tournament results found');
          return navigate('/tournaments');
        }
        // Ensure there are matches for this tournament; otherwise, find the next with matches
        const { count: matchCount } = await supabase
          .from('matches')
          .select('*', { count: 'exact', head: true })
          .eq('tournament_id', latestTournament.id);
        if ((matchCount || 0) > 0) {
          return navigate(`/tournaments/${latestTournament.id}`);
        }
        // Try to find any recent completed tournament that has matches
        const { data: candidates } = await supabase
          .from('tournaments')
          .select('id')
          .eq('is_completed', true)
          .order('completed_at', { ascending: false })
          .limit(5);
        if (candidates && candidates.length) {
          for (const c of candidates) {
            const { count } = await supabase
              .from('matches')
              .select('*', { count: 'exact', head: true })
              .eq('tournament_id', c.id);
            if ((count || 0) > 0) {
              return navigate(`/tournaments/${c.id}`);
            }
          }
        }
        toast.error('No tournament results found');
        return navigate('/tournaments');
      } catch (error) {
        console.error('Error fetching latest tournament:', error);
        return navigate('/tournaments');
      }
    }

    // Standard navigation for other types
    navigate(notification.actionUrl);
  };

  return { handleNotificationClick };
};
